/**
 * «Собрать кропы» — seed the SELECTED zone's pooled crops so it can be labelled at all.
 *
 * The zone comes from the annotator's own picker, which now lists configured-but-empty
 * zones at (0); this dialog deliberately has no zone list of its own. Two pickers for
 * one choice is two ways to be looking at different zones — and the whole point of the
 * (0) entries is that the main picker is now complete.
 *
 * Nothing here labels anything. Crops arrive with every head unset, which is exactly
 * what puts them in the «Неразмеченные» queue and keeps them out of training until a
 * human has judged them.
 */
import { Select } from '@/components/ui/select';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { Download, Loader2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useT } from '@/contexts/I18nContext';
import {
  getCollectPrecheck,
  getCollectRuns,
  launchCollect,
  type CollectRun,
} from '@/api/tablePool';
import { Tooltip } from '@/components/ui/tooltip';

/** The three calls a domain must provide. Tables are the default so the existing call
 *  site is untouched; shelves pass their own. Only the endpoints and the "nothing is
 *  configured" wording differ — everything else about a collection is identical, so the
 *  dialog is shared rather than copied. */
export interface CollectApi {
  precheck: (zoneId: number, days: number) => Promise<{ data: CollectPrecheckLike }>;
  launch: (zoneId: number, target: number, days: number) => Promise<unknown>;
  runs: (zoneId?: number) => Promise<{ data: { runs: CollectRunLike[] } }>;
}

/** The subset of a precheck this dialog reads. `objects` is the domain-neutral count
 *  (tables here, shelves there) that both backends report. */
export interface CollectPrecheckLike {
  available: number;
  pooled: number;
  objects: number;
}

/** The subset of a run row this dialog renders — identical across domains except for
 *  the per-object breakdown, which is not shown here. */
export type CollectRunLike = Omit<CollectRun, 'per_table'>;

const TABLE_API: CollectApi = {
  precheck: getCollectPrecheck,
  launch: launchCollect,
  runs: getCollectRuns,
};

const WINDOWS = [7, 30, 90, 180];
const SIZES = [200, 500, 1000, 3000];
/** Poll while a run is in flight — a collection is S3 copies, i.e. seconds to minutes. */
const RUNNING_POLL_MS = 3000;

function RunLine({ run, t }: { run: CollectRunLike; t: (k: string) => string }) {
  const when = new Date(run.created_at).toLocaleString();
  const period =
    `${new Date(run.date_from).toLocaleDateString()}–${new Date(run.date_to).toLocaleDateString()}`;
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="text-muted-foreground">{when}</span>
      <span className="text-muted-foreground">{period}</span>
      {run.status === 'running' && (
        <span className="flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('data.collect.runRunning')}
        </span>
      )}
      {run.status === 'done' && (
        <>
          <span className="font-medium">+{run.collected}</span>
          {run.skipped_existing > 0 && (
            <span className="text-muted-foreground">
              {t('data.collect.runSkipped').replace('{n}', String(run.skipped_existing))}
            </span>
          )}
          {run.failed > 0 && (
            <span className="text-amber-700">
              {t('data.collect.runFailed').replace('{n}', String(run.failed))}
            </span>
          )}
        </>
      )}
      {run.status === 'failed' && (
        <Tooltip content={run.error}><span className="text-destructive">
          {t('data.collect.runError')}
        </span></Tooltip>
      )}
      {run.launched_by && <span className="text-muted-foreground">· {run.launched_by}</span>}
    </li>
  );
}

export default function CollectCropsDialog({
  zoneId,
  zoneName,
  onCollected,
  api = TABLE_API,
  noObjectsKey = 'data.collect.noTables',
  descriptionKey = 'data.collect.description',
  swrKey = 'collect',
}: {
  /** The zone selected in the annotator. null disables the button — there is nothing
   *  sensible to collect "for no zone". */
  zoneId: number | null;
  zoneName?: string;
  /** Called once a run finishes so the caller can refresh its zone list: a zone at (0)
   *  only stops being empty here. */
  onCollected?: () => void;
  /** Domain endpoints. Defaults to tables so their call site needs no change. */
  api?: CollectApi;
  /** i18n key for "this zone has none of the thing being collected". */
  noObjectsKey?: string;
  /** i18n key for the dialog blurb — it names what is being collected (столы / полки). */
  descriptionKey?: string;
  /** Namespaces the SWR cache. Without it the two domains would share `['collect-…',
   *  zoneId]` and one annotator would render the other's precheck for the same zone. */
  swrKey?: string;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [target, setTarget] = useState(1000);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: pre, isLoading: preLoading } = useSWR(
    open && zoneId != null ? [`${swrKey}-precheck`, zoneId, days] : null,
    () => api.precheck(zoneId as number, days).then(r => r.data),
  );

  const { data: runsResp, mutate: reloadRuns } = useSWR(
    open && zoneId != null ? [`${swrKey}-runs`, zoneId] : null,
    () => api.runs(zoneId as number).then(r => r.data),
  );
  const runs = runsResp?.runs ?? [];
  const active = runs.some(r => r.status === 'running');

  // Only polls while something is actually running, and stops on its own when the run
  // lands — a dialog left open must not sit there hitting the API forever.
  useEffect(() => {
    if (!open || !active) return;
    const id = setInterval(() => { void reloadRuns(); }, RUNNING_POLL_MS);
    return () => clearInterval(id);
  }, [open, active, reloadRuns]);

  // When the last run finishes, tell the caller: "the zone stops showing (0)" is the
  // whole point of pressing the button.
  const [wasActive, setWasActive] = useState(false);
  useEffect(() => {
    if (active) setWasActive(true);
    else if (wasActive) {
      setWasActive(false);
      onCollected?.();
    }
  }, [active, wasActive, onCollected]);

  const willCollect = Math.min(target, pre?.available ?? 0);

  const onLaunch = async () => {
    if (zoneId == null) return;
    setLaunching(true);
    setError(null);
    try {
      await api.launch(zoneId, target, days);
      await reloadRuns();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || t('data.collect.launchFailed'));
    } finally {
      setLaunching(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={zoneId == null}
        onClick={() => setOpen(true)}
      >
        <Download className="mr-1.5 h-3.5 w-3.5" />
        {t('data.collect.button')}
      </Button>

      {open && (
        <Dialog open onOpenChange={o => !o && setOpen(false)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('data.collect.title')}</DialogTitle>
              <DialogDescription>{t(descriptionKey)}</DialogDescription>
            </DialogHeader>

            <div className="space-y-3 text-sm">
              {/* Shown, not chosen: the zone is whatever the annotator is pointed at. */}
              <div className="flex items-baseline justify-between rounded-md border px-3 py-2">
                <span className="text-muted-foreground">{t('data.collect.zone')}</span>
                <span className="font-medium">📍 {zoneName ?? `zone ${zoneId}`}</span>
              </div>

              <div className="flex gap-3">
                <label className="flex-1">
                  <span className="mb-1 block font-medium">{t('data.collect.period')}</span>
                  <Select
                    value={days}
                    onChange={e => setDays(Number(e.target.value))}
                  >
                    {WINDOWS.map(d => (
                      <option key={d} value={d}>
                        {t('data.collect.lastDays').replace('{n}', String(d))}
                      </option>
                    ))}
                  </Select>
                </label>
                <label className="flex-1">
                  <span className="mb-1 block font-medium">{t('data.collect.size')}</span>
                  <Select
                    value={target}
                    onChange={e => setTarget(Number(e.target.value))}
                  >
                    {SIZES.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </Select>
                </label>
              </div>

              {/* The ceiling, stated before launching: `available` is what the frame
                  pipeline actually banked for this window, and it — not the requested
                  size — is usually what decides the result. */}
              <div className="rounded-md border px-3 py-2">
                {preLoading && (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </span>
                )}
                {pre && !preLoading && (
                  <>
                    <div className="flex justify-between">
                      <span>{t('data.collect.available')}</span>
                      <span className="font-medium">{pre.available}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{t('data.collect.alreadyPooled')}</span>
                      <span>{pre.pooled}</span>
                    </div>
                    <div className="mt-1 flex justify-between border-t pt-1">
                      <span>{t('data.collect.willCollect')}</span>
                      <span className="font-medium">{willCollect}</span>
                    </div>
                    {pre.objects === 0 && (
                      <div className="mt-1.5 flex items-start gap-2 text-destructive">
                        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{t(noObjectsKey)}</span>
                      </div>
                    )}
                    {pre.objects > 0 && pre.available === 0 && (
                      <div className="mt-1.5 flex items-start gap-2 text-amber-700">
                        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{t('data.collect.noneAvailable')}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {runs.length > 0 && (
                <div>
                  <div className="mb-1 font-medium">{t('data.collect.history')}</div>
                  <ul className="max-h-40 space-y-0.5 overflow-y-auto text-xs">
                    {runs.map(r => <RunLine key={r.id} run={r} t={t} />)}
                  </ul>
                </div>
              )}

              {error && <div className="text-destructive">{error}</div>}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                {t('common.close')}
              </Button>
              <Button
                onClick={onLaunch}
                disabled={launching || active || zoneId == null || willCollect === 0}
              >
                {(launching || active) && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {active ? t('data.collect.runRunning') : t('data.collect.launch')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
